// Blocked numbers: the CMS view of the inbound blocklist. A blocked caller
// hears a short explanation twice and is then declined without ringing anyone
// (goserver app/external/twilio_call/blocked_numbers.go).
//
// Read is open to every Twilio Agent user, because "why did that caller say
// they cannot reach us" is a question anyone on the page may have to answer.
// Adding, lifting and re-applying a block is CARE or SUPER_USER, enforced by
// the server on every mutation; the gate below only decides whether the
// controls render, so a stale access_type can never do more than show a button
// that returns 403.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaBan, FaHistory, FaSyncAlt, FaUndo } from 'react-icons/fa';
import { set_notification_variable as setNotificationVariable } from '../../../../redux/action_creators/home_actions';
import CommonHeader from '../../../util_components/common_header';
import {
  activateBlockedNumber,
  addBlockedNumber,
  apiErrorReason,
  deactivateBlockedNumber,
  fetchBlockedNumbers,
  fetchBlockedNumberHistory
} from '../settings_api';
import useArmedConfirm from '../settings_hooks';

const HISTORY_PAGE_SIZE = 25;

// What the add form reports back. The server answers 200 for all three, because
// two care agents reacting to the same caller is the expected case and the
// second one needs to be told, not shown a failure.
const ADD_STATE_MESSAGE = {
  added: 'Number blocked.',
  reactivated: 'Number blocked again. The caller will hear the message once more.',
  already_active: 'That number is already blocked.'
};

// access_type is a comma-separated MySQL SET, so match a token and not the
// whole string. Deliberately not the isSuperUser helper in the parent page:
// blocking a nuisance caller is care work, and routing it through a super-user
// would put it out of reach of the team that needs it.
const canManageBlockedNumbers = (accessType) =>
  (accessType || '')
    .split(',')
    .map((t) => t.trim().toUpperCase())
    .some((t) => t === 'SUPER_USER' || t === 'CARE');

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);

  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const cellStyle = { padding: '8px 12px', verticalAlign: 'top' };

const pillStyle = (active) => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 9999,
  fontSize: 12,
  fontWeight: 500,
  color: active ? 'var(--state-error-text)' : 'var(--text-secondary)',
  background: active ? 'var(--state-error-bg)' : 'var(--surface-sunken)'
});

const actionButtonStyle = (danger) => ({
  padding: '4px 10px',
  borderRadius: 9999,
  fontSize: 12,
  fontWeight: 600,
  border: '1px solid var(--border-default)',
  background: danger ? 'var(--state-error-bg)' : 'var(--surface-card)',
  color: danger ? 'var(--state-error-text)' : 'var(--text-primary)',
  cursor: 'pointer'
});

export default function BlockedNumbers() {
  const dispatch = useDispatch();
  const accessType = useSelector((s) => s.home.access_type);
  const canManage = useMemo(() => canManageBlockedNumbers(accessType), [accessType]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(false);

  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');
  const [addError, setAddError] = useState('');

  const [history, setHistory] = useState([]);
  const [historyNumber, setHistoryNumber] = useState(null);
  const [historyDone, setHistoryDone] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  const { armedId, arm, disarm } = useArmedConfirm();

  const toastError = useCallback(
    (err, fallback) =>
      dispatch(setNotificationVariable(true, 'error', apiErrorReason(err, fallback))),
    [dispatch]
  );

  const loadNumbers = useCallback(() => {
    setLoading(true);
    fetchBlockedNumbers(showAll ? 'all' : 'active')
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((e) => toastError(e, 'Could not load the blocked numbers.'))
      .finally(() => setLoading(false));
  }, [showAll, toastError]);

  // Reloaded from scratch rather than appended when the filter changes: the
  // cursor is only meaningful within one filter, and carrying it across would
  // page into a feed the user is no longer looking at.
  const loadHistory = useCallback(
    (numberUuid, cursor) => {
      setHistoryLoading(true);
      // Clear before the request, not after it resolves. Between the click and
      // the response the old feed is still on screen, and Load more in that
      // window would send the previous feed's cursor into the new filter.
      if (!cursor) {
        setHistory([]);
        setHistoryDone(false);
      }
      fetchBlockedNumberHistory({
        numberUuid,
        cursorDate: cursor ? cursor.created_date : undefined,
        cursorId: cursor ? cursor.id : undefined,
        limit: HISTORY_PAGE_SIZE
      })
        .then((data) => {
          const page = Array.isArray(data) ? data : [];
          setHistory((prev) => (cursor ? [...prev, ...page] : page));
          // A short page means the feed is exhausted. Asking for one more page
          // to prove it would be a round trip that always returns nothing.
          setHistoryDone(page.length < HISTORY_PAGE_SIZE);
        })
        .catch((e) => toastError(e, 'Could not load the history.'))
        .finally(() => setHistoryLoading(false));
    },
    [toastError]
  );

  useEffect(() => {
    loadNumbers();
  }, [loadNumbers]);

  useEffect(() => {
    loadHistory(historyNumber ? historyNumber.uuid : '', null);
  }, [historyNumber, loadHistory]);

  const reloadBoth = () => {
    loadNumbers();
    loadHistory(historyNumber ? historyNumber.uuid : '', null);
  };

  const onAdd = () => {
    const trimmed = phone.trim();
    setAddError('');
    if (!trimmed) {
      setAddError('Enter a phone number.');

      return;
    }

    setBusy(true);
    addBlockedNumber(trimmed, reason.trim())
      .then((res) => {
        dispatch(
          setNotificationVariable(
            true,
            res.state === 'already_active' ? 'info' : 'success',
            ADD_STATE_MESSAGE[res.state] || 'Number blocked.'
          )
        );
        setPhone('');
        setReason('');
        reloadBoth();
      })
      .catch((e) => setAddError(apiErrorReason(e, 'Could not block that number.')))
      .finally(() => setBusy(false));
  };

  const onToggle = (row) => {
    setBusy(true);
    disarm();
    const call = row.is_active ? deactivateBlockedNumber : activateBlockedNumber;
    call(row.uuid, '')
      .then(() => {
        dispatch(
          setNotificationVariable(
            true,
            'success',
            row.is_active ? 'Block lifted.' : 'Number blocked again.'
          )
        );
        reloadBoth();
      })
      .catch((e) => toastError(e, 'Could not change that block.'))
      .finally(() => setBusy(false));
  };

  const renderRow = (row) => (
    <tr key={row.uuid} className="border-b border-line">
      <td style={cellStyle}>
        <button
          type="button"
          onClick={() => setHistoryNumber(row)}
          title="Show this number's history"
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: 'var(--text-primary)',
            fontWeight: 600
          }}
        >
          {row.phone_number}
        </button>
      </td>
      <td style={cellStyle}>{row.reason || '—'}</td>
      <td style={cellStyle}>
        <span style={pillStyle(!!row.is_active)}>{row.is_active ? 'Blocked' : 'Lifted'}</span>
      </td>
      <td style={cellStyle}>{row.times_blocked}</td>
      <td style={cellStyle}>{formatDate(row.last_called_at)}</td>
      <td style={cellStyle}>{row.blocked_by_name || '—'}</td>
      <td style={cellStyle}>{formatDate(row.created_date)}</td>
      {canManage && (
        <td style={cellStyle}>
          {armedId === row.uuid ? (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() => onToggle(row)}
                style={actionButtonStyle(!!row.is_active)}
              >
                Confirm
              </button>
              <button
                type="button"
                onClick={disarm}
                style={{ ...actionButtonStyle(false), marginLeft: 6 }}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() => arm(row.uuid)}
              style={actionButtonStyle(!!row.is_active)}
            >
              {row.is_active ? 'Lift block' : 'Block again'}
            </button>
          )}
        </td>
      )}
    </tr>
  );

  return (
    <CommonHeader title="Blocked numbers" loading={loading}>
      <div className="p-4">
        {!canManage && (
          <div
            className="mb-4 rounded-lg p-3 text-sm"
            style={{ background: 'var(--surface-sunken)', color: 'var(--text-secondary)' }}
          >
            These numbers can only be added or edited by the CARE team or a SUPER_USER.
          </div>
        )}

        {canManage && (
          <div className="mb-5 rounded-lg border border-line bg-surface-card p-4">
            <div className="mb-2 flex items-center gap-2 font-semibold">
              <FaBan size={14} color="var(--state-error-text)" />
              Block a number
            </div>
            <div className="flex flex-wrap items-start gap-2">
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+14155550123"
                className="rounded border border-line px-3 py-2 text-sm"
                style={{ background: 'var(--surface-card)', color: 'var(--text-primary)' }}
              />
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason (optional)"
                className="flex-1 rounded border border-line px-3 py-2 text-sm"
                style={{ background: 'var(--surface-card)', color: 'var(--text-primary)' }}
              />
              <button
                type="button"
                disabled={busy}
                onClick={onAdd}
                style={actionButtonStyle(true)}
                className="py-2"
              >
                Block
              </button>
            </div>
            {/* Inline, not a toast: the number they typed is still on screen and
                the fix is in that field. */}
            {addError ? (
              <div className="mt-2 text-sm" style={{ color: 'var(--state-error-text)' }}>
                {addError}
              </div>
            ) : null}
            <div className="mt-2 text-xs" style={{ color: 'var(--text-muted)' }}>
              The caller hears a short message twice, then their calls are declined silently.
            </div>
          </div>
        )}

        <div className="mb-2 flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm" htmlFor="blocked-show-all">
            <input
              id="blocked-show-all"
              type="checkbox"
              checked={showAll}
              onChange={() => setShowAll((v) => !v)}
            />
            Show lifted blocks too
          </label>
          <button type="button" onClick={reloadBoth} style={actionButtonStyle(false)}>
            <FaSyncAlt size={11} /> Refresh
          </button>
        </div>

        <div className="overflow-x-auto rounded-lg border border-line bg-surface-card">
          <table className="min-w-full text-sm">
            <thead className="bg-surface-sunken text-content-secondary">
              <tr>
                <th style={cellStyle}>Number</th>
                <th style={cellStyle}>Reason</th>
                <th style={cellStyle}>Status</th>
                <th style={cellStyle}>Times called</th>
                <th style={cellStyle}>Last call</th>
                <th style={cellStyle}>Added by</th>
                <th style={cellStyle}>Added on</th>
                {canManage && <th style={cellStyle}>Action</th>}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading ? (
                <tr>
                  <td style={cellStyle} colSpan={canManage ? 8 : 7}>
                    No numbers are blocked.
                  </td>
                </tr>
              ) : (
                rows.map(renderRow)
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-6">
          <div className="mb-2 flex items-center gap-2 font-semibold">
            <FaHistory size={14} color="var(--text-secondary)" />
            {historyNumber ? `History for ${historyNumber.phone_number}` : 'History'}
            {historyNumber && (
              <button
                type="button"
                onClick={() => setHistoryNumber(null)}
                style={actionButtonStyle(false)}
              >
                <FaUndo size={11} /> All numbers
              </button>
            )}
          </div>
          <div className="overflow-x-auto rounded-lg border border-line bg-surface-card">
            <table className="min-w-full text-sm">
              <thead className="bg-surface-sunken text-content-secondary">
                <tr>
                  <th style={cellStyle}>When</th>
                  <th style={cellStyle}>Number</th>
                  <th style={cellStyle}>Action</th>
                  <th style={cellStyle}>By</th>
                  <th style={cellStyle}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 && !historyLoading ? (
                  <tr>
                    <td style={cellStyle} colSpan={5}>
                      Nothing yet.
                    </td>
                  </tr>
                ) : (
                  history.map((h) => (
                    <tr key={h.id} className="border-b border-line">
                      <td style={cellStyle}>{formatDate(h.created_date)}</td>
                      <td style={cellStyle}>{h.phone_number}</td>
                      <td style={cellStyle}>{h.action}</td>
                      <td style={cellStyle}>{h.actor_name || '—'}</td>
                      <td style={cellStyle}>{h.reason || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!historyDone && history.length > 0 && (
            <button
              type="button"
              disabled={historyLoading}
              onClick={() =>
                loadHistory(historyNumber ? historyNumber.uuid : '', history[history.length - 1])
              }
              style={{ ...actionButtonStyle(false), marginTop: 8 }}
            >
              {historyLoading ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      </div>
    </CommonHeader>
  );
}
